/**
 * <VersioningPanel> — releases as git tags, and the LIVE servers that run them.
 *
 * The deployment model: the Twasta platform is where the application is built
 * and tested. Its live copies run on their own servers, from source pulled
 * from git, and each live server upgrades itself by checking out a release's
 * tag. So "versioning" here means: every release is a tag pushed to the
 * project's repository — nothing on the platform is pinned, and no database
 * is split.
 *
 * Three parts:
 *   1. START VERSIONING (once) — the working copy as it is becomes the
 *      baseline release, tagged and, when a repository is linked, pushed. The
 *      server's dry run is shown first, in plain words.
 *   2. LIVE DEPLOYMENTS — one per intake key: which version each runs, whether
 *      it is behind, when it last reported, how many issues came from it, and
 *      the READ-ONLY connection the support AI uses to look at its data when it
 *      analyses a report from there.
 *   3. INTAKE KEYS — minting one IS creating a deployment: the live server is
 *      configured with it, forwards its users' issues with it, and reports its
 *      version with it. Shown once, stored hashed.
 */

import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import {
  AlertTriangle, Check, Copy, Database, GitBranch, KeyRound, Pencil, PlayCircle, Plus, RefreshCw,
  Server, Trash2, X,
} from 'lucide-react'

import type {
  DeploymentsPage, IntakeKey, LiveDataConfig, LiveDeployment, MintedIntakeKey, ProjectConnection,
  SupportMeta, SupportNotify, SupportRuntime, VersioningStatus,
} from './contract'
import {
  Card, Empty, ErrorLine, Pill, Spinner, SupportScope, btn, errorText, fmtDateTime, inputStyle,
  labelStyle, mutedText, timeAgo,
} from './ui'

export interface VersioningPanelProps {
  runtime: SupportRuntime
  meta: SupportMeta
  notify?: SupportNotify
}

export default function VersioningPanel({ runtime, meta, notify }: VersioningPanelProps) {
  const [status, setStatus] = useState<VersioningStatus | null>(null)
  const [error, setError] = useState('')
  // Bumped when a key is minted or revoked: a key IS a deployment.
  const [deploymentsKey, setDeploymentsKey] = useState(0)
  const isPm = meta.me.capacities.includes('pm')

  const load = useCallback(async () => {
    if (!runtime.versioningStatus) return
    try {
      setStatus(await runtime.versioningStatus())
      setError('')
    } catch (e) {
      setError(errorText(e, 'The versioning status could not be loaded.'))
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  if (!runtime.versioningStatus) {
    return <SupportScope><Empty>Versioning is managed on the platform.</Empty></SupportScope>
  }

  return (
    <SupportScope>
      <ErrorLine message={error} onRetry={() => { void load() }} />
      {!status && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading…</div>}
      {status && !status.enabled && (
        <StartVersioning status={status} runtime={runtime} isPm={isPm} notify={notify}
          onStarted={() => { void load() }} />
      )}
      {status && status.enabled && (
        <Card title="Release management is on" icon={<GitBranch size={15} />}
          actions={status.latest ? <Pill tone="success">latest v{status.latest}</Pill> : undefined}>
          <div style={mutedText}>
            Every release is a git tag pushed to this project's repository. Live servers upgrade themselves
            by checking out that tag; the platform stays your development and test environment.
          </div>
        </Card>
      )}
      {runtime.listDeployments && (
        <LiveDeployments key={deploymentsKey} runtime={runtime} isPm={isPm} notify={notify} />
      )}
      {runtime.listIntakeKeys && isPm && (
        <IntakeKeys runtime={runtime} notify={notify} onChanged={() => setDeploymentsKey((k) => k + 1)} />
      )}
    </SupportScope>
  )
}

// ── not versioned yet ──────────────────────────────────────────────────────

function StartVersioning({ status, runtime, isPm, notify, onStarted }: {
  status: VersioningStatus
  runtime: SupportRuntime
  isPm: boolean
  notify?: SupportNotify
  onStarted: () => void
}) {
  const preview = status.preview || {}
  const [version, setVersion] = useState(String(preview.version || '1.0.0'))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const linked = preview.repository === 'linked'

  const start = async () => {
    if (!runtime.startVersioning) return
    if (!window.confirm(
      `Start release management at v${version}?\n\n`
      + 'The working copy is committed as the baseline and tagged '
      + `v${version}${linked ? ', and the tag is pushed to the linked repository' : ' (locally — no repository is linked yet)'}.`,
    )) return
    setBusy(true)
    setError('')
    try {
      const res = await runtime.startVersioning({ version: version.trim() || '1.0.0' })
      const push = res.release?.push_status
      notify?.success(res.already
        ? `Already under release management (v${res.version || version}).`
        : `Release management started at v${res.release?.version || version}`
          + (push === 'pushed' ? ' — the tag is pushed.' : push === 'failed'
            ? ' — but the tag could not be pushed; see Releases.' : ' — the tag is local until a repository is linked.'))
      onStarted()
    } catch (e) {
      setError(errorText(e, 'Versioning could not be started.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Start versioning" icon={<GitBranch size={15} />}>
      <p style={{ marginTop: 0 }}>
        The platform is where this application is built and tested. Its <strong>live</strong> copies run on
        their own servers, from source pulled from git. Release management connects the two:
      </p>
      <ul style={{ marginTop: 0, paddingLeft: 18 }}>
        <li>
          The application as it is now is committed as the baseline and tagged <strong>v{version || '1.0.0'}</strong>
          {linked ? ', and the tag is pushed to the linked repository.' : '.'}
        </li>
        <li>
          Fixes for reported issues are built and tested on their own branch, then bundled into releases from
          the <em>Releases</em> tab. Each release is a new tag.
        </li>
        <li>
          A live server upgrades itself by checking out a release tag. Nothing on the platform is pinned and
          nothing about your development database changes.
        </li>
      </ul>
      {!linked && preview.repository && (
        <div style={{ color: 'var(--sp-warning)', fontSize: '0.8625rem', marginBottom: 6 }}>
          <AlertTriangle size={12} style={{ verticalAlign: -1 }} /> No repository is linked — the tag stays on the
          platform, and live servers cannot pull it until you link one (Advanced → Source control).
        </div>
      )}
      {preview.git_repo === 'will be created' && (
        <div style={{ ...mutedText, marginBottom: 6 }}>The project has no git history yet — it will be created.</div>
      )}
      {preview.uncommitted_changes && (
        <div style={{ ...mutedText, marginBottom: 6 }}>
          Uncommitted changes in the working copy become part of the baseline commit.
        </div>
      )}
      <ErrorLine message={status.preview_error || ''} />

      <div style={{ maxWidth: 180, margin: '10px 0' }}>
        <label style={labelStyle} htmlFor="twsp-start-version">First version</label>
        <input id="twsp-start-version" value={version} onChange={(e) => setVersion(e.target.value)}
          style={inputStyle} disabled={!isPm} />
      </div>
      <ErrorLine message={error} />
      {isPm && runtime.startVersioning ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={btn('primary')} disabled={busy || !!status.preview_error}
            onClick={() => { void start() }}>
            {busy ? <Spinner size={13} /> : <PlayCircle size={14} />} Start versioning
          </button>
        </div>
      ) : (
        <div style={mutedText}>A project manager starts versioning.</div>
      )}
    </Card>
  )
}

// ── live deployments ───────────────────────────────────────────────────────

function connectionName(conns: ProjectConnection[], id?: string | null): string {
  if (!id) return ''
  const c = conns.find((x) => x.id === id)
  return c ? `${c.name || c.id}${c.type ? ` (${c.type})` : ''}` : id
}

function LiveDeployments({ runtime, isPm, notify }: {
  runtime: SupportRuntime
  isPm: boolean
  notify?: SupportNotify
}) {
  const [page, setPage] = useState<DeploymentsPage | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!runtime.listDeployments) return
    setLoading(true)
    try {
      setPage(await runtime.listDeployments())
      setError('')
    } catch (e) {
      setError(errorText(e, 'The live deployments could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  const deployments = page?.deployments || []
  const conns = page?.connections || []
  return (
    <Card title="Live deployments" icon={<Server size={15} />}
      actions={(
        <button type="button" style={btn('ghost', true)} onClick={() => { void load() }} title="Refresh">
          {loading ? <Spinner size={12} /> : <RefreshCw size={12} />}
        </button>
      )}>
      <p style={{ ...mutedText, marginTop: 0 }}>
        Each server running this application live, as it last reported in. A deployment appears when it is
        given an intake key (below); it reports issues and its version with that key.
      </p>
      <ErrorLine message={error} onRetry={() => { void load() }} />
      {page && deployments.length === 0 && <div style={mutedText}>No live deployment yet — mint an intake key for one.</div>}
      {deployments.map((d) => (
        <div key={d.id} style={{
          border: '1px solid var(--sp-border)', borderRadius: 9, padding: '0.6rem 0.7rem', marginBottom: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong>{d.label || d.id}</strong>
            <code style={mutedText}>{d.id}</code>
            {d.current_version ? <Pill tone="accent">v{d.current_version}</Pill> : <Pill>version unknown</Pill>}
            {d.behind && (
              <Pill tone="warning" title={d.latest_version ? `v${d.latest_version} is released` : undefined}>
                behind{d.latest_version ? ` — v${d.latest_version} available` : ''}
              </Pill>
            )}
            <span style={{ flex: 1 }} />
            <span style={mutedText}>
              {d.last_used_at ? `last contact ${timeAgo(d.last_used_at)}` : 'never connected'}
              {typeof d.issues === 'number' ? ` · ${d.issues} issue${d.issues === 1 ? '' : 's'}` : ''}
            </span>
            {isPm && runtime.configureDeployment && (
              <button type="button" style={btn('ghost', true)}
                onClick={() => setEditing(editing === d.id ? null : d.id)}>
                {editing === d.id ? <X size={12} /> : <Pencil size={12} />} {editing === d.id ? 'Close' : 'Configure'}
              </button>
            )}
          </div>
          <div style={{ ...mutedText, marginTop: 4 }}>
            <Database size={11} style={{ verticalAlign: -1 }} />{' '}
            {d.live_data?.connection_id
              ? <>Live data: {connectionName(conns, d.live_data.connection_id)} · {d.live_data.tenant_database === 'fixed'
                ? 'one shared database' : 'one database per tenant'}
                {d.live_data.tenant_connections && Object.keys(d.live_data.tenant_connections).length
                  ? ` · ${Object.keys(d.live_data.tenant_connections).length} tenant override(s)` : ''}</>
              : 'No live-data connection — the AI analyses reports from here against the development copy only.'}
            {d.git?.branch ? <> · <GitBranch size={11} style={{ verticalAlign: -1 }} /> {d.git.branch}</> : null}
          </div>
          {editing === d.id && (
            <DeploymentEditor deployment={d} connections={conns} runtime={runtime} notify={notify}
              onSaved={() => { setEditing(null); void load() }} />
          )}
        </div>
      ))}
    </Card>
  )
}

const radioRow: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4, cursor: 'pointer' }

function DeploymentEditor({ deployment, connections, runtime, notify, onSaved }: {
  deployment: LiveDeployment
  connections: ProjectConnection[]
  runtime: SupportRuntime
  notify?: SupportNotify
  onSaved: () => void
}) {
  const ld: LiveDataConfig = deployment.live_data || {}
  const [label, setLabel] = useState(deployment.label || '')
  const [connectionId, setConnectionId] = useState(ld.connection_id || '')
  const [tenantDb, setTenantDb] = useState<string>(ld.tenant_database || 'per_tenant')
  const [overrides, setOverrides] = useState<Array<{ tenant: string; connection: string }>>(
    Object.entries(ld.tenant_connections || {}).map(([tenant, connection]) => ({ tenant, connection })))
  const [branch, setBranch] = useState(deployment.git?.branch || '')
  const [notes, setNotes] = useState(deployment.git?.notes || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    if (!runtime.configureDeployment) return
    const tenant_connections: Record<string, string> = {}
    for (const o of overrides) {
      const t = o.tenant.trim()
      if (!t && !o.connection) continue
      if (!t || !o.connection) { setError('Each tenant override needs both a tenant id and a connection.'); return }
      tenant_connections[t] = o.connection
    }
    setBusy(true)
    setError('')
    try {
      await runtime.configureDeployment(deployment.id, {
        label: label.trim() || undefined,
        live_data: { connection_id: connectionId || null, tenant_database: tenantDb, tenant_connections },
        git: { branch: branch.trim() || null, notes: notes.trim() || null },
      })
      notify?.success(`Deployment "${label.trim() || deployment.id}" saved.`)
      onSaved()
    } catch (e) {
      setError(errorText(e, 'The deployment could not be saved.'))
    } finally {
      setBusy(false)
    }
  }

  const select = (value: string, onChange: (v: string) => void, aria: string, emptyLabel = '— None —') => (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} aria-label={aria}>
      <option value="">{emptyLabel}</option>
      {connections.map((c) => (
        <option key={c.id} value={c.id}>{c.name || c.id}{c.type ? ` (${c.type})` : ''}</option>
      ))}
    </select>
  )

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--sp-border)' }}>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Name</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} style={inputStyle}
          placeholder="e.g. Acme production" />
      </div>

      <div style={labelStyle}>Live data — how the support AI reads this deployment's data</div>
      <div style={{
        fontSize: '0.8425rem', padding: '0.5rem 0.6rem', borderRadius: 8, marginBottom: 8,
        border: '1px solid var(--sp-warning)',
      }}>
        When someone reports an issue from this deployment, the AI looks at the records involved to find
        the cause. Use a <strong>read-only</strong> connection for that: a database user that can only
        SELECT, or a Database Gateway on the live server's network. The AI never writes through it, and a
        read-only user makes sure it cannot.
      </div>
      {connections.length === 0 ? (
        <div style={{ ...mutedText, marginBottom: 8 }}>
          No connections are registered for this project. Add one under Analytics → Connections (or set up a
          Database Gateway), then come back here.
        </div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle}>Connection</label>
          {select(connectionId, setConnectionId, 'Live data connection')}
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <label style={radioRow}>
          <input type="radio" name={`twsp-tdb-${deployment.id}`} checked={tenantDb !== 'fixed'}
            onChange={() => setTenantDb('per_tenant')} style={{ marginTop: 3 }} />
          <span>
            <strong>One database per tenant</strong>
            <span style={{ display: 'block', ...mutedText }}>
              The live server keeps each organization in its own database (twasta_preview_&lt;tenant&gt;); the
              AI reads the reporter's tenant's database through the connection.
            </span>
          </span>
        </label>
        <label style={radioRow}>
          <input type="radio" name={`twsp-tdb-${deployment.id}`} checked={tenantDb === 'fixed'}
            onChange={() => setTenantDb('fixed')} style={{ marginTop: 3 }} />
          <span>
            <strong>One shared database</strong>
            <span style={{ display: 'block', ...mutedText }}>Every tenant's data is in the connection's own database.</span>
          </span>
        </label>
      </div>
      {connections.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Per-tenant connections (optional)</label>
          <div style={{ ...mutedText, marginBottom: 4 }}>
            For a tenant whose data lives somewhere else entirely — its own server, say.
          </div>
          {overrides.map((o, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 6, marginBottom: 4 }}>
              <input value={o.tenant} placeholder="tenant id" style={inputStyle} aria-label="Tenant id"
                onChange={(e) => setOverrides((cur) => cur.map((x, j) => (j === i ? { ...x, tenant: e.target.value } : x)))} />
              {select(o.connection, (v) => setOverrides((cur) => cur.map((x, j) => (j === i ? { ...x, connection: v } : x))),
                'Connection for this tenant', '— Choose —')}
              <button type="button" style={btn('ghost', true)} aria-label="Remove override"
                onClick={() => setOverrides((cur) => cur.filter((_, j) => j !== i))}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button type="button" style={btn('ghost', true)}
            onClick={() => setOverrides((cur) => [...cur, { tenant: '', connection: '' }])}>
            <Plus size={12} /> Add tenant override
          </button>
        </div>
      )}

      <div style={labelStyle}>Source</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, marginBottom: 10 }}>
        <input value={branch} onChange={(e) => setBranch(e.target.value)} style={inputStyle}
          placeholder="branch or tag prefix" aria-label="Git branch" />
        <input value={notes} onChange={(e) => setNotes(e.target.value)} style={inputStyle}
          placeholder="notes (where it runs, who maintains it…)" aria-label="Notes" />
      </div>

      <ErrorLine message={error} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" style={btn('primary', true)} disabled={busy} onClick={() => { void save() }}>
          {busy ? <Spinner size={12} /> : <Check size={12} />} Save deployment
        </button>
      </div>
    </div>
  )
}

// ── intake keys ────────────────────────────────────────────────────────────

function IntakeKeys({ runtime, notify, onChanged }: {
  runtime: SupportRuntime
  notify?: SupportNotify
  onChanged: () => void
}) {
  const [keys, setKeys] = useState<IntakeKey[] | null>(null)
  const [label, setLabel] = useState('')
  const [minted, setMinted] = useState<MintedIntakeKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!runtime.listIntakeKeys) return
    try {
      setKeys((await runtime.listIntakeKeys()).keys || [])
      setError('')
    } catch (e) {
      setError(errorText(e, 'The intake keys could not be loaded.'))
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  const mint = async () => {
    if (!runtime.mintIntakeKey || !label.trim()) return
    setBusy(true)
    setError('')
    try {
      const k = await runtime.mintIntakeKey(label.trim())
      setMinted(k)
      setCopied(false)
      setLabel('')
      void load()
      onChanged()
    } catch (e) {
      setError(errorText(e, 'The key could not be created.'))
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (k: IntakeKey) => {
    if (!runtime.revokeIntakeKey) return
    if (!window.confirm(`Revoke "${k.label}"? That deployment stops forwarding issues and reporting its `
      + 'version until it is given a new key.')) return
    try {
      await runtime.revokeIntakeKey(k.id)
      notify?.success(`Key "${k.label}" revoked.`)
      void load()
      onChanged()
    } catch (e) {
      setError(errorText(e, 'The key could not be revoked.'))
    }
  }

  const copy = async () => {
    if (!minted) return
    try {
      await navigator.clipboard.writeText(minted.key)
      setCopied(true)
    } catch {
      notify?.error('Copy failed — select the key and copy it by hand.')
    }
  }

  return (
    <Card title="Intake keys" icon={<KeyRound size={15} />}>
      <p style={{ ...mutedText, marginTop: 0 }}>
        One key per live deployment. Configure it on that server (its administrator's Support &amp; updates
        page): the server then forwards its users' issues here and reports which version it runs. Revoking a
        key removes the deployment.
      </p>
      {minted && (
        <div style={{ border: '1px solid var(--sp-success)', borderRadius: 8, padding: '0.6rem', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Key for “{minted.label}” — copy it now; it is never shown again.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input readOnly value={minted.key} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8125rem' }}
              onFocus={(e) => e.currentTarget.select()} />
            <button type="button" style={btn('subtle', true)} onClick={() => { void copy() }}>
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" style={btn('ghost', true)} onClick={() => setMinted(null)}>Done</button>
          </div>
        </div>
      )}
      <ErrorLine message={error} />
      {keys === null && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner size={13} /> Loading…</div>}
      {keys && keys.length === 0 && <div style={{ ...mutedText, marginBottom: 8 }}>No keys yet.</div>}
      {keys && keys.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
          {keys.map((k) => (
            <li key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.3rem 0', borderBottom: '1px solid var(--sp-border)' }}>
              <KeyRound size={12} />
              <strong>{k.label}</strong>
              <code style={mutedText}>{k.id}</code>
              {k.revoked_at ? <Pill tone="error">revoked</Pill> : <Pill tone="success">active</Pill>}
              <span style={{ ...mutedText, flex: 1 }}>
                created {fmtDateTime(k.created_at)}{k.last_used_at ? ` · last used ${timeAgo(k.last_used_at)}` : ' · never used'}
              </span>
              {!k.revoked_at && runtime.revokeIntakeKey && (
                <button type="button" style={btn('danger', true)} onClick={() => { void revoke(k) }}>
                  <Trash2 size={12} /> Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {runtime.mintIntakeKey && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} style={inputStyle}
            placeholder="Deployment name, e.g. “Acme production”"
            onKeyDown={(e) => { if (e.key === 'Enter') void mint() }} />
          <button type="button" style={btn('primary', true)} disabled={busy || !label.trim()} onClick={() => { void mint() }}>
            {busy ? <Spinner size={12} /> : <Plus size={12} />} Mint key
          </button>
        </div>
      )}
    </Card>
  )
}
